<template>
  <div
    v-if="!isScheduledActivity && themeLoaded"
    style="height: 100%"
    :class="isSpeedyKid ? 'speedy-kid-award' : ''"
  >
    <h1 v-if="totalBlocks" aria-live="polite" role="region" class="award-title">
      {{ totalBlocksCompleted }} out of {{ totalBlocks }} Activities Completed
    </h1>
    <div v-if="!isSpeedyKid">
      <ul class="blockHolder">
        <award-block
          v-for="(item, index) in behaviour"
          :active="item.active"
          :animate="item.animate"
          :bgcolor="item.bgColor"
          :blockIndex="index + 1"
          :key="`awardblock-${index}`"
          :theme="item.theme"
          :totalNumberOfBlocks="behaviour.length"
        ></award-block>
      </ul>
      <button
        @click="getNextActivity"
        aria-label="Continue"
        class="award-button-continue"
        ref="continuebutton"
        tabindex="0"
        v-html="button"
      ></button>
    </div>
    <div v-else class="blockAndFlagHolderContainer">
      <div class="flagHolder">
        <div class="flagRow">
          <div class="pyramidFlag" :style="{ backgroundImage: `url(${pyramidFlag}), none` }"></div>
        </div>
        <div class="flagRow">
          <div class="starFiller star21" :class="{ done: star21 }"></div>
        </div>
        <div class="flagRow">
          <div class="starFiller star19" :class="{ done: star19 }"></div>
          <div class="starFiller star20" :class="{ done: star20 }"></div>
        </div>
        <div class="flagRow">
          <div class="starFiller star16" :class="{ done: star16 }"></div>
          <div class="starFiller star17" :class="{ done: star17 }"></div>
          <div class="starFiller star18" :class="{ done: star18 }"></div>
        </div>
        <div class="flagRow">
          <div class="starFiller star12" :class="{ done: star12 }"></div>
          <div class="starFiller star13" :class="{ done: star13 }"></div>
          <div class="starFiller star14" :class="{ done: star14 }"></div>
          <div class="starFiller star15" :class="{ done: star15 }"></div>
        </div>
        <div class="flagRow">
          <div class="starFiller star7" :class="{ done: star7 }"></div>
          <div class="starFiller star8" :class="{ done: star8 }"></div>
          <div class="starFiller star9" :class="{ done: star9 }"></div>
          <div class="starFiller star10" :class="{ done: star10 }"></div>
          <div class="starFiller star11" :class="{ done: star11 }"></div>
        </div>
        <div class="flagRow">
          <div class="starFiller star1" :class="{ done: star1 }"></div>
          <div class="starFiller star2" :class="{ done: star2 }"></div>
          <div class="starFiller star3" :class="{ done: star3 }"></div>
          <div class="starFiller star4" :class="{ done: star4 }"></div>
          <div class="starFiller star5" :class="{ done: star5 }"></div>
          <div class="starFiller star6" :class="{ done: star6 }"></div>
        </div>
      </div>
      <button
        @click.stop.prevent="getNextActivity"
        tabindex="0"
        aria-label="Continue"
        class="award-button-continue"
        ref="continuebutton"
        v-html="button"
      ></button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import { TweenMax } from 'gsap';
import { range } from 'lodash';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import btnContinue from 'components/svg-components/theme-progress-btn.svg';
import pFlag from 'images/speedykid/pyramidFlag.png';
import Background from 'images/spotCheckBg.png';
import Ribbon from 'images/spotcheck-results/ribbon.png';
import Quit from 'assets/svg/spotCheckQuit.svg';
import AwardBlock from './awardblock.vue';
import KeyboardingAPI from '../../api';

export default {
  mixins: [AccessibilityHelper, ErrorLogger],
  components: {
    AwardBlock
  },
  data() {
    return {
      progressDone: false,
      button: btnContinue,
      behaviour: [],
      star1: false,
      star2: false,
      star3: false,
      star4: false,
      star5: false,
      star6: false,
      star7: false,
      star8: false,
      star9: false,
      star10: false,
      star11: false,
      star12: false,
      star13: false,
      star14: false,
      star15: false,
      star16: false,
      star17: false,
      star18: false,
      star19: false,
      star20: false,
      star21: false,
      pyramidFlag: pFlag,
      bg: Background,
      svgQuit: Quit,
      ribbon: Ribbon,
      themeLoaded: false,
      totalBlocks: 0,
      totalBlocksCompleted: 0
    };
  },

  computed: {
    ...mapState('student', ['username', 'position']),
    ...mapState('student', {
      sessionId: state => state.sessionId,
      lastPosition: state => state.position.last || {},
      nextPosition: state => state.position.next || {}
    }),
    ...mapState('application', ['kwtMode']),
    ...mapState('activity', {
      activityInfo: state => ({
        activityId: state.data.activityId || null,
        scheduledActivityId: state.data.scheduledActivityId || null,
        activityType: state.data.type
      }),
      teacherAssist: state => state.teacherAssist,
      teacherAction: state => state.teacherAction,
      isSpotCheck: state => Boolean(state.data.spotCheck)
    }),

    ...mapGetters('student', { theme: 'previousTheme' }),

    motivational() {
      return `Keep up the good work${this.username ? `, ${this.username}!` : '!'}`;
    },

    isSpeedyKid() {
      return this.theme && this.theme.id === 'SpeedyKid';
    },

    isScheduledActivity() {
      const { scheduledActivityId } = this.activityInfo;
      return Boolean(scheduledActivityId);
    },

    isLastActivityInCurriculum() {
      const {
        lastPosition: {
          progress: { curriculum: { position, total } = {} } = {},
          theme: { id: prevThemeId } = {}
        },
        nextPosition: { theme: { id: themeId } = {} }
      } = this;

      return (
        position &&
        total &&
        position === total &&
        themeId === 'SpeedyKid' &&
        prevThemeId !== 'SpeedyKid' &&
        !this.isScheduledActivity
      );
    }
  },

  mounted() {
    this.init();
  },

  methods: {
    ...mapMutations('student', ['setCurriculumPosition']),

    ...mapMutations('activity', [
      'resetViewedIntro',
      'setActivityRestarting',
      'resetActivityStore',
      'resetProgress'
    ]),

    ...mapMutations('keyboard', ['resetKeyboard']),

    ...mapMutations('application', ['setModal', 'setToolbarMode']),

    async init() {
      this.setToolbarMode('award');
      if (!this.isSpotCheck) {
        await this.sendData();
      }

      // if this is an assessment, push user automatically
      if (this.isScheduledActivity) {
        this.getNextActivity();
        return;
      }

      if (this.isLastActivityInCurriculum) {
        this.setModal({ type: 'cert' });
      }

      if (!this.isSpeedyKid) {
        const { progress: { week: { position: completed, total } = {} } = {} } = this.lastPosition;

        this.totalBlocks = total;
        this.totalBlocksCompleted = completed;

        this.behaviour = range(total).map(i => ({
          theme: this.theme.id,
          bgColor: this.theme.color,
          animate: i === completed - 1,
          active: i < completed
        }));
      } else {
        const { progress: { theme: { position: currentPosition } = {} } = {} } = this.lastPosition;

        this.totalBlocks = 21;
        this.totalBlocksCompleted = currentPosition;

        let starsToShow = currentPosition;
        this.earnedPyramids = Math.floor(starsToShow / 21);

        while (starsToShow >= 21) {
          starsToShow -= 21;
        }
        for (let i = starsToShow; i > 0; i -= 1) {
          const starName = `star${i}`;
          this[starName] = true;
        }

        this[`star${starsToShow}`] = true;
      }

      this.setScreenReaderAlert(
        `Great job! You have completed activity number ${this.totalBlocksCompleted}.`
      );

      this.themeLoaded = true;
      this.$nextTick(() => this.showContinueBtn());
    },

    async sendData() {
      const {
        duration,
        numberOfAttempts,
        numberOfChars,
        numberOfNonLetters,
        errors,
        responseData
      } = this.getErrorLogger();

      const { activityId, scheduledActivityId, activityType } = this.activityInfo;

      const {
        grade,
        nextPosition: { asn, week }
      } = this;

      if (!activityId && !scheduledActivityId) return;

      let progressData = {
        responseData,
        elapsedTime: duration,
        numberOfAttempts,
        numberOfChars,
        numberOfNonLetters,
        errorData: errors,
        complete: true,
        grade,
        activityId,
        ...(this.isSpeedyKid && { speedyKid: true })
      };

      if (this.isScheduledActivity) {
        const { teacherAssist, teacherAction } = this;

        progressData = {
          ...progressData,
          teacherAssist,
          teacherAction
        };
      } else {
        progressData = {
          ...progressData,
          asn,
          week
        };
      }

      if (this.isScheduledActivity) {
        const { status, data } = await KeyboardingAPI.addScheduledActivityData(
          activityType,
          this.sessionId,
          scheduledActivityId,
          progressData
        );
        if (status < 400) {
          this.sendDataComplete(data, status);
        }
      } else {
        const { status, data } = await KeyboardingAPI.addProgressData(
          activityType,
          this.sessionId,
          progressData
        );
        if (status < 400) {
          this.sendDataComplete(data, status);
        } else {
          this.$store.commit('application/setModal', { type: 'error' }, { root: true });
        }
      }
    },

    sendDataComplete(data, statusCode) {
      if (statusCode < 400) {
        const { position } = data;
        this.setCurriculumPosition(position);
      }
    },

    showContinueBtn() {
      TweenMax.to('.award-button-continue', 1, { css: { right: -7 } });
    },

    getNextActivity() {
      this.resetActivityStore();
      this.resetKeyboard();
      this.resetProgress();
      this.resetViewedIntro();

      this.$nextTick(() => {
        this.$router.replace({ path: '/curriculum', params: {} });
      });
    }
  }
};
/* eslint-disable max-len */
</script>
<style lang="scss" scope>
.contentPanel {
  width: 100%;
}
.award-title {
  margin: 0.67em auto;
  text-align: center;
  width: 60%;
}
.blockHolder {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  list-style: none;
  margin: 50px auto 0;
  max-width: 800px;
  padding-left: 0px;
  text-align: center;
  width: 80%;
}

.speedy-kid-award {
  min-height: 100%;
  height: max-content;
  min-width: 100%;
  width: max-content;
}

.blockAndFlagHolderContainer {
  height: calc(100% - 90px);
  overflow: auto;

  .flagHolder {
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    height: calc(768px - 90px);
    max-height: 768px;
    font-size: 0;
  }
}

.flagRow {
  display: block;
  text-align: center;
  font-size: 0;
}

.starFiller {
  font-size: 0;
  width: 97px;
  height: 100px;
  display: inline-block;
  background-position: top left;
  background-image: url(data:image/png;base64,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);
}

.starFiller.done {
  background-position: top right;
}

.pyramidFlag {
  height: 60px;
  background-repeat: no-repeat;
  background-position: top center;
  background-size: contain;
}

.spotCheckNext {
  text-align: right;
  width: 800px;
  margin: 0 auto;

  button.spotCheckButton {
    padding: 5px;
    display: inline-block;

    &:hover span {
      color: #fef2d2;
    }

    svg {
      width: 100px;
      height: 100px;
    }

    span {
      display: block;
      color: white;
      text-align: center;
      font-family: hwtfutura-semibold;
      font-size: 18px;
    }
  }
}

.myBest {
  position: absolute;
  top: 0;
  left: 400px;
  width: 200px;
  /*background-color: red;*/
  text-align: center;

  .heading {
    display: block;
    font-size: 24px;
    margin-top: 25px;
  }

  .bestWpm {
    font-family: hwtfutura-semibold;
    font-size: 72px;
  }

  .bestAccuracy {
    font-family: hwtfutura-semibold;
    font-size: 48px;
  }

  .newBest {
    font-size: 21px;
    margin: 10px 0;
    color: #2e70be;
    visibility: hidden;

    &.show {
      visibility: visible;
    }
  }

  .ribbon {
    visibility: hidden;
    position: absolute;
    top: -50px;
    right: -40px;
    height: 140px;
    width: 80px;

    &.show {
      visibility: visible;
    }
  }
}

.tablet {
  .blockAndFlagHolderContainer {
    height: auto;

    .flagHolder {
      height: 85%;
    }
  }
}
</style>
